/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import { Fragment, useEffect, useState } from 'react';

import { Button, Typography2 } from '@coursera/cds-core';
import { ArrowNextIcon } from '@coursera/cds-icons';
import { useTracker } from '@coursera/event-pulse/react';

import _t from 'i18n!nls/embedded-experience';

import type { NewTabFallbackReasonCode } from '../lib/embeddedEventTypes';
import { readEmbeddedOrgSlug } from '../lib/embeddedLaunchContext';

/**
 * `routeBlocked` — a route that must never render inside an LMS iframe embed (see
 * `isRouteBlockedInIframe`); a full-viewport standalone page that opens the current URL in a new tab.
 * `ssoFallback` — the SSO pop-out fallback shown inside the login layout when the frame can't
 * complete sign-in; an inline card whose `onContinue` opens the (single-use) SSO-return URL.
 */
type Variant = 'routeBlocked' | 'ssoFallback';

type Props = {
  variant: Variant;
  /** Required for `ssoFallback` (opens the single-use SSO-return URL); `routeBlocked` opens the current URL. */
  onContinue?: () => void;
  /** Why the caller is showing this page instead of the iframe experience, for `view_newtab_stage`. */
  fallbackReasonCode?: NewTabFallbackReasonCode;
};

const styles = {
  routeBlocked: css`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: var(--cds-spacing-200);
    min-height: 100vh;
    padding: var(--cds-spacing-400);
    text-align: center;
  `,
  ssoFallback: css`
    display: flex;
    flex-direction: column;
    gap: var(--cds-spacing-300);
    max-width: 449px;
  `,
  textBlock: css`
    display: flex;
    flex-direction: column;
    gap: var(--cds-spacing-100);
    color: var(--cds-color-neutral-primary);
  `,
};

const IframeBlockerPage = ({ variant, onContinue, fallbackReasonCode }: Props) => {
  // Opening the new tab redeems a single-use SSO token, so the button can't be reused. Once clicked,
  // switch to a terminal message telling the learner to reload the LMS to try again (which mints a
  // new token).
  const [opened, setOpened] = useState(false);
  const track = useTracker();

  useEffect(() => {
    track('view_newtab_stage', {
      stage: 'cta',
      reasonCode: fallbackReasonCode ?? variant,
      enterprise: { organizationSlug: readEmbeddedOrgSlug() },
    });
    // track is the useTracker() no-args form, memoized on a constant dep array, so it's stable here.
  }, [fallbackReasonCode, track, variant]);

  const handleContinue = () => {
    track('click_button', { button: { name: 'embedded_newtab_open_cta' } });
    // `routeBlocked` has no token to hand off, so it just opens the current URL in a new tab.
    (onContinue ?? (() => window.open(window.location.href, '_blank', 'noopener,noreferrer')))();
    setOpened(true);
    track('view_newtab_stage', { stage: 'confirmation', enterprise: { organizationSlug: readEmbeddedOrgSlug() } });
  };

  const content = (
    <Fragment>
      {!opened && (
        <Fragment>
          <div css={styles.textBlock}>
            <Typography2 component="h1" variant="titleMedium">
              {variant === 'ssoFallback' ? _t('Open Coursera in a new tab') : _t('This page isn’t available here')}
            </Typography2>
            <Typography2 component="p" variant="bodyPrimary">
              {variant === 'ssoFallback'
                ? _t(
                    "We couldn't sign you in to Coursera here. Please continue in the new tab to complete the sign-in process."
                  )
                : _t('Open it on Coursera to keep going.')}
            </Typography2>
          </div>
          {variant === 'ssoFallback' ? (
            <Button
              icon={<ArrowNextIcon />}
              iconPosition="after"
              size="medium"
              variant="primary"
              onClick={handleContinue}
            >
              {_t('Continue in New Tab')}
            </Button>
          ) : (
            <Button size="medium" variant="primary" onClick={handleContinue}>
              {_t('Open on Coursera')}
            </Button>
          )}
        </Fragment>
      )}
      <div css={[opened && styles.textBlock]} role="status">
        {opened ? (
          <Fragment>
            <Typography2 component="h1" variant="titleMedium">
              {_t('Coursera opened in a new tab')}
            </Typography2>
            <Typography2 component="p" variant="bodyPrimary">
              {variant === 'ssoFallback'
                ? _t('Continue in the new tab. To sign in here again, reload this page.')
                : _t('Continue in the new tab. To open it here again, reload this page.')}
            </Typography2>
          </Fragment>
        ) : null}
      </div>
    </Fragment>
  );

  return variant === 'routeBlocked' ? (
    <main css={styles.routeBlocked} data-testid="IframeBlockerPage">
      {content}
    </main>
  ) : (
    <div css={styles.ssoFallback} data-testid="OpenInNewTabPage">
      {content}
    </div>
  );
};

export default IframeBlockerPage;
